import { useCallback, useEffect, useState } from "react";
import { ImagePlus, Pencil, Plus, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { TeamDialog } from "@/components/TeamDialog";
import { useAuth } from "@/context/AuthContext";
import { AffiliationPanel } from "@/components/AffiliationPanel";
import { TeamBadges } from "@/components/TeamBadges";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Teams() {
  const [teams, setTeams] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [showHidden, setShowHidden] = useState(false);
  const { church, refresh } = useAuth();
  const shown = teams.filter((t) => showHidden || !t.hidden);
  const toggleHide = (t) => run(() => api.put(`/teams/${t.team_id}`, { name: t.name, description: t.description, gifts: t.gifts, disc: t.disc, hidden: !t.hidden }), t.hidden ? "Shown to volunteers" : "Hidden from matches");

  const load = useCallback(() => api.get("/teams").then((r) => setTeams(r.data)), []);
  useEffect(() => { load(); api.get("/reference").then((r) => setGifts(r.data.gifts)); }, [load]);

  const giftName = (k) => gifts.find((g) => g.key === k)?.name || k;

  const run = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
      return true;
    } catch {
      toast.error("Something went wrong. Please try again.");
      return false;
    }
  };

  const save = async (f) => {
    const ok = await run(() => (edit ? api.put(`/teams/${edit.team_id}`, f) : api.post("/teams", f)), edit ? "Team updated" : "Team added");
    if (ok) setOpen(false);
  };

  const uploadPhoto = async (team, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    await run(() => api.post(`/teams/${team.team_id}/photo`, fd), "Photo updated");
  };

  const onConfirm = () => {
    const c = confirm;
    setConfirm(null);
    if (c.kind === "reset") run(() => api.post("/teams/reset").then((r) => { refresh(); return r; }), "Ministry library restored to Dream Teams defaults");
    else run(() => api.delete(`/teams/${c.team.team_id}`), "Team deleted");
  };

  return (
    <div className="fade-up">
      <p className="eyebrow">Customize your teams</p>
      <div className="flex flex-wrap items-end justify-between gap-4 mt-2">
        <div>
          <h1 className="font-display text-navy text-4xl font-semibold tracking-tight">Teams</h1>
          <p className="text-muted-ink mt-2 max-w-xl">The ministry teams volunteers get matched into. Edit the defaults or add new ones to match your church.</p>
        </div>
        <div className="flex gap-3">
          <button className="btn-ghost !py-2 flex items-center gap-2" onClick={() => setConfirm({ kind: "reset" })} data-testid="reset-teams-btn"><RotateCcw size={16} /> Reset to defaults</button>
          <button className="btn-navy !py-2 flex items-center gap-2" onClick={() => { setEdit(null); setOpen(true); }} data-testid="add-team-btn"><Plus size={16} /> Add team</button>
        </div>
      </div>

      {church?.affiliation_enabled !== false && <AffiliationPanel onApplied={load} />}
      <label className="flex items-center gap-2 text-sm text-navy mt-8" data-testid="show-hidden-toggle">
        <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />
        Show hidden teams, ministries and church offices ({teams.filter((t) => t.hidden).length})
      </label>
      <div className="grid md:grid-cols-2 gap-5 mt-6">
        {shown.map((t) => (
          <div key={t.team_id} className={`card-soft p-7 lift flex flex-col overflow-hidden ${t.hidden ? "opacity-60" : ""}`} data-testid={`team-card-${t.team_id}`}>
            {t.photo_file_id && <img src={mediaUrl(t.photo_file_id)} alt={t.name} className="-mx-7 -mt-7 mb-6 w-[calc(100%+3.5rem)] max-w-none h-40 object-cover" data-testid={`team-photo-${t.team_id}`} />}
            <div className="flex items-start justify-between gap-4">
              <h3 className="font-display text-navy text-xl font-semibold">{t.name}</h3>
              <span className="text-xs bg-teal-soft text-teal rounded-full px-3 py-1 whitespace-nowrap" data-testid={`team-matched-${t.team_id}`}>{t.matched} matched</span>
            </div>
            <TeamBadges t={t} />
            <p className="text-muted-ink mt-2 leading-relaxed text-[15px] flex-1">{t.description}</p>
            <div className="flex flex-wrap gap-1.5 mt-5">
              {t.gifts.map((g) => <span key={g} className="text-xs border border-line rounded-full px-2.5 py-1 text-navy">{giftName(g)}</span>)}
              {t.disc.map((d) => <span key={d} className="text-xs bg-navy text-white rounded-full px-2.5 py-1">{d}</span>)}
            </div>
            <div className="flex gap-4 mt-5 pt-5 border-t border-line text-sm">
              <button className="text-navy hover:text-teal flex items-center gap-1.5" onClick={() => { setEdit(t); setOpen(true); }} data-testid={`edit-team-${t.team_id}`}><Pencil size={14} /> Edit</button>
              <label className="text-navy hover:text-teal flex items-center gap-1.5 cursor-pointer" data-testid={`upload-team-photo-${t.team_id}`}>
                <ImagePlus size={14} /> {t.photo_file_id ? "Change photo" : "Add photo"}
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid={`team-photo-input-${t.team_id}`}
                  onChange={(e) => { uploadPhoto(t, e.target.files[0]); e.target.value = ""; }} />
              </label>
              {(t.kind || "position") === "position" && <button className="text-navy hover:text-teal" onClick={() => toggleHide(t)} data-testid={`toggle-hide-${t.team_id}`}>{t.hidden ? "Show" : "Hide"}</button>}
              <button className="text-red-700 hover:opacity-70 flex items-center gap-1.5" onClick={() => setConfirm({ kind: "delete", team: t })} data-testid={`delete-team-${t.team_id}`}><Trash2 size={14} /> Delete</button>
            </div>
          </div>
        ))}
      </div>
      <TeamDialog open={open} onOpenChange={setOpen} team={edit} gifts={gifts} onSave={save} />
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.kind === "reset" ? "Reset to the original Dream Teams library?" : `Delete ${confirm?.team?.name}?`}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.kind === "reset" ? "Your active ministry library will be restored to the original Dream Teams defaults, and your church's affiliation will be cleared. Current teams are archived, not deleted. Completed assessments keep their original results." : "Volunteer matches will be recalculated without this team."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirm} data-testid="confirm-action-btn">Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
